import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type Props = {
  /** URL absolue encodée dans le QR */
  url: string;
  /** Nom du fichier PNG (sans extension), caractères spéciaux neutralisés */
  downloadBasename?: string;
  className?: string;
  /** Si true : panneau QR ouvert dès le montage (ex. panneau admin). */
  defaultExpanded?: boolean;
};

function safeBasename(name: string): string {
  const t = name.trim() || 'lien-public';
  return t.replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 80) || 'lien-public';
}

/**
 * QR code pour un lien public + bouton téléchargement PNG.
 * Utilisé dans les panneaux de partage (formation, podcast, parcours).
 */
export default function PublicShareQrSection({
  url,
  downloadBasename = 'lien-public',
  className,
  defaultExpanded = false,
}: Props) {
  const { t } = useTranslation();
  const panelId = useId();
  const triggerId = `${panelId}-trigger`;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setDataUrl(null);
    setError(false);
  }, [url]);

  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed || !expanded) {
      return;
    }
    let cancelled = false;
    setError(false);
    setDataUrl(null);
    void (async () => {
      try {
        const QR = await import('qrcode');
        const d = await QR.toDataURL(trimmed, {
          width: 220,
          margin: 2,
          errorCorrectionLevel: 'M',
          color: { dark: '#0f172a', light: '#ffffff' },
        });
        if (!cancelled) setDataUrl(d);
      } catch {
        if (!cancelled) {
          setDataUrl(null);
          setError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, expanded]);

  const downloadPng = useCallback(() => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${safeBasename(downloadBasename)}.png`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [dataUrl, downloadBasename]);

  if (!url.trim()) return null;

  return (
    <div className={cn('border-t border-slate-200/80 pt-3 dark:border-gray-600/80', className)}>
      <button
        id={triggerId}
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded-lg py-1.5 text-start text-xs font-semibold uppercase tracking-wide text-slate-600 outline-none ring-offset-2 hover:bg-slate-50/80 focus-visible:ring-2 focus-visible:ring-[color:var(--inst-player-primary,#0C1C38)] dark:text-gray-400 dark:hover:bg-gray-800/50"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span>{t('share.publicQrTitle')}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 dark:text-gray-500',
            expanded && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="pt-2">
            {error ? (
              <p className="text-xs text-red-600 dark:text-red-400">{t('share.publicQrError')}</p>
            ) : !dataUrl ? (
              <div className="flex justify-center py-6" aria-busy="true">
                <Loader2 className="h-7 w-7 animate-spin text-slate-400 dark:text-gray-500" aria-hidden />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 pb-1">
                <img
                  src={dataUrl}
                  alt=""
                  width={220}
                  height={220}
                  className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-600 dark:bg-white"
                />
                <button
                  type="button"
                  onClick={downloadPng}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-800 shadow-sm hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <Download className="h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)]" aria-hidden />
                  {t('share.downloadQrPng')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
